import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions, PermissaoValue } from '../hooks/usePermissions';

interface PermissionRouteProps {
  children: React.ReactNode;
  /** Permissão única requerida para acessar a rota */
  permission?: PermissaoValue;
  /** Lista de permissões - usuário precisa ter TODAS para acessar */
  permissions?: PermissaoValue[];
  /** Lista de permissões - usuário precisa ter PELO MENOS UMA para acessar */
  anyPermission?: PermissaoValue[];
  /** Rota para redirecionar caso não tenha permissão (padrão: /dashboard) */
  redirectTo?: string;
  /** Se true, mostra mensagem de acesso negado ao invés de redirecionar */
  showAccessDenied?: boolean;
}

const AccessDeniedMessage: React.FC = () => (
  <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
    <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center max-w-md">
      <svg
        className="w-16 h-16 text-red-500 mx-auto mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <h2 className="text-xl font-semibold text-red-700 mb-2">
        Acesso Negado
      </h2>
      <p className="text-red-600">
        Você não possui permissão para acessar esta página.
      </p>
      <p className="text-sm text-red-500 mt-2">
        Entre em contato com um administrador caso precise de acesso.
      </p>
    </div>
  </div>
);

/**
 * Componente para proteger rotas baseado em permissões do usuário.
 *
 * Pode ser usado de três formas:
 * 1. `permission` - Requer uma permissão específica
 * 2. `permissions` - Requer TODAS as permissões da lista
 * 3. `anyPermission` - Requer PELO MENOS UMA das permissões da lista
 *
 * @example
 * // Requer permissão específica
 * <PermissionRoute permission={PERMISSOES.MILITARES_LISTAR}>
 *   <MilitaresPage />
 * </PermissionRoute>
 *
 * @example
 * // Requer todas as permissões
 * <PermissionRoute permissions={[PERMISSOES.MILITARES_LISTAR, PERMISSOES.MILITARES_CRIAR]}>
 *   <MilitaresPage />
 * </PermissionRoute>
 *
 * @example
 * // Requer pelo menos uma das permissões
 * <PermissionRoute anyPermission={[PERMISSOES.MILITARES_LISTAR, PERMISSOES.MILITARES_VISUALIZAR]}>
 *   <MilitaresPage />
 * </PermissionRoute>
 */
export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  permission,
  permissions,
  anyPermission,
  redirectTo = '/dashboard',
  showAccessDenied = true,
}) => {
  const { hasPermission, hasAllPermissions, hasAnyPermission } = usePermissions();

  // Verifica se o usuário tem as permissões necessárias
  let hasAccess = true;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    hasAccess = hasAllPermissions(permissions);
  } else if (anyPermission && anyPermission.length > 0) {
    hasAccess = hasAnyPermission(anyPermission);
  }

  // Se não tem acesso, mostra mensagem ou redireciona
  if (!hasAccess) {
    if (showAccessDenied) {
      return <AccessDeniedMessage />;
    }
    return <Navigate to="/acesso-negado" replace />;
  }

  // Tem acesso, renderiza o conteúdo
  return <>{children}</>;
};

export default PermissionRoute;
